import { PreviewEmailMessage } from "@/schema"
import { format } from "date-fns"
import { ArrowDownLeft, ArrowUpRight, Calendar, ExternalLink, User } from "lucide-react"

import { usePeekViewData } from "@/hooks/api/peek-views/queries"
import { Link as BookLink } from "@/components/book-link"

import PeekViewSkeleton from "./skeleton"

export default function SyncboxMessagePeekView({
  syncboxMessageId,
  href,
}: {
  syncboxMessageId: string
  href: string | null
}) {
  const { data: email, isLoading } = usePeekViewData(syncboxMessageId, "Syncbox::Message") as {
    data: PreviewEmailMessage
    isLoading: boolean
  }

  if (isLoading) return <PeekViewSkeleton className="max-w-80" />

  const dateText = email.delivered_at ? format(email.delivered_at, "MMM d, yyyy · h:mm a") : "Not delivered"
  const isOutbound = email.direction === "outbound"

  return (
    <div className="text-tertiary-foreground flex w-full max-w-80 flex-col items-stretch gap-2">
      <div className="mb-0.5 flex items-baseline gap-2">
        {isOutbound ? (
          <ArrowUpRight className="text-blue-accent mt-0.5 size-4 flex-none" />
        ) : (
          <ArrowDownLeft className="text-green-accent mt-0.5 size-4 flex-none" />
        )}
        <p className="line-clamp-2 font-medium">{email.subject || "No subject"}</p>
        {href && (
          <BookLink
            href={href}
            className="text-muted-foreground hover:text-link ml-auto flex-none transition-colors"
            onClick={(e) => e.stopPropagation()}
            aria-label="Open in new page"
          >
            <ExternalLink className="size-3.5" strokeWidth={1.5} />
          </BookLink>
        )}
      </div>
      {email.snippet && <p className="text-muted-foreground line-clamp-2 text-sm">{email.snippet}</p>}
      <div className="flex items-center gap-2">
        <Calendar className="text-icon size-4 flex-none" />
        <p className="text-sm">{dateText}</p>
      </div>
      {email.from_email && (
        <div className="flex items-center gap-2">
          <User className="text-icon size-4 flex-none" />
          <p className="truncate text-sm">{email.from_email}</p>
        </div>
      )}
    </div>
  )
}
